// src/slices/userInfoSlice.ts
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { fetchAllPermittedCustomers, fetchAllCustomerSites, fetchUserInfo, fetchGeneralDetails, logoutUser, fetchWeatherData, fetchLocationFromHistory } from '../services/customerService';
import type { UserInfo, UserInfoState, CustomerSite, GeneralDetails, AddressLocation } from '../types/customerTypes';
import { normalizeComponentName, normalizePreferenceKeys } from '../../utils/permissionUtils';
import dayjs from 'dayjs';

const initialState: UserInfoState = {
    weatherInfo: null,
    weatherInfoError: '',
    route: '',
    weatherInfoLoading: false,
    collapsed: false,
    selectedKey: '',
    FactoryPath: false,
    ExecutivePath: false,
    MaintenancePath: false,
    LogsPath: false,
    RemotePath: false,
    ParentCustomer: false,
    theme: 0,
    userInfo: null,
    loading: false,
    error: null,
    selectedSite: '',
    selectedCustomerName: '',
    errorLogout: null,
    loadingLogout: false,
    detailedSites: [],
    detailedSitesLoading: false,
    detailedSitesError: null,
    loggedOut: false,
    permittedCustomers: [],
    generalDetailsLoading: false,
    generalDetailsError: null,
    permittedCustomersLoading: false,
    fromDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('day').format('YYYY-MM-DD'),
    permittedCustomersError: '',
    customerSites: [], // Initial state for customer sites
    customerSitesLoading: false,
    customerSitesError: null,
    generalDetails: {} as GeneralDetails,
    selectedTimezone: 'US/Pacific',
    selectedTimezoneAbb: 'PST',
    loadingLocationHistory: false,
    locationHistoryError: null,
    locationHistory: [],
};

const userInfoSlice = createSlice({
    name: 'userInfo',
    initialState,
    reducers: {
        clearUserInfo(state) {
            // state.userInfo = null;
            state.generalDetails = {} as GeneralDetails;
            state.locationHistory = [];
            state.locationHistoryError = null;
            state.loadingLocationHistory = false;
            state.selectedTimezone = 'US/Pacific';
            state.selectedTimezoneAbb = 'PST';
            state.weatherInfo = null;
            state.weatherInfoError = '';
            state.weatherInfoLoading = false;
            state.loading = false;
            state.error = null;
            // state.selectedCustomerName = '';
            state.generalDetailsLoading = false;
            state.detailedSites = [];
            state.theme = 0;
            // state.detailedSitesLoading = false;
            state.detailedSitesError = null;
            state.selectedSite = '';
            // state.permittedCustomers = [];
            // state.permittedCustomersLoading = false;
            // state.permittedCustomersError = null;
            state.customerSites = []; // Reset customer sites when clearing user info
            state.customerSitesLoading = false;
            state.customerSitesError = null;
            state.fromDate = dayjs().startOf('day').format('YYYY-MM-DD');
            state.toDate = dayjs().endOf('day').format('YYYY-MM-DD');
        },
        clearWeatherInfo(state) {
            state.weatherInfo = null;
            state.weatherInfoError = '';
            state.weatherInfoLoading = false;
        },
        setSelectedCustomerName(state, action: PayloadAction<string>) {
            state.selectedCustomerName = action.payload;
            state.ExecutivePath = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.executive === true);
            state.FactoryPath = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.factory === true);
            state.MaintenancePath = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.maintenance === true);
            state.LogsPath = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.globalLogs === true);
            state.RemotePath = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.remote === true);
            state.ParentCustomer = state.permittedCustomers.some((customer) => customer.customer === action.payload && customer.preferences?.parent === true);
        },
        setSelectedSite(state, action: PayloadAction<string>) {
            state.selectedSite = action.payload;
        },
        setSelectedTimezone(state, action: PayloadAction<string>) {
            state.selectedTimezone = action.payload;
        },
        setSelectedTimezoneAbb(state, action: PayloadAction<string>) {
            state.selectedTimezoneAbb = action.payload;
        },
        setIconTheme(state, action: PayloadAction<number>) {
            state.theme = action.payload;
        },
        setFromDate(state, action: PayloadAction<string>) {
            state.fromDate = action.payload;
        },
        setToDate(state, action: PayloadAction<string>) {
            state.toDate = action.payload;
        },
        clearPermittedCustomers(state) {
            state.permittedCustomers = [];
        },
        setCollapsed(state, action: PayloadAction<boolean>) {
            state.collapsed = action.payload;
        },
        setSelectedKey(state, action: PayloadAction<string>) {
            state.selectedKey = action.payload;
        },
        setSelectedRoute(state, action: PayloadAction<string>) {
            state.selectedKey = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<UserInfo>) => {
               
                state.loading = false;
                state.userInfo = action.payload;
            })
            .addCase(fetchUserInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchWeatherData.pending, (state) => {
                // Only show loading on initial fetch (when data is null)
                if (state.weatherInfo === null) {
                    state.weatherInfoLoading = true;
                }
                state.weatherInfoError = '';
            })
            .addCase(fetchWeatherData.fulfilled, (state, action) => {
                state.weatherInfoLoading = false;
                state.weatherInfo = action.payload;
            })
            .addCase(fetchWeatherData.rejected, (state, action) => {
                state.weatherInfoLoading = false;
                state.weatherInfoError = action.payload as string;
            })
            .addCase(logoutUser.pending, (state) => {
                state.loadingLogout = true;
                state.errorLogout = null;
            })
            .addCase(logoutUser.fulfilled, (state) => {
                state.loadingLogout = true;
                state.loggedOut = true;
            })
            .addCase(logoutUser.rejected, (state, action) => {
                state.loadingLogout = false;
                state.errorLogout = action.payload as string || 'Logout failed';
            })
            .addCase(fetchAllPermittedCustomers.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.permittedCustomers.length === 0) {
                    state.permittedCustomersLoading = true;
                }
            })
            .addCase(fetchAllPermittedCustomers.fulfilled, (state, action) => {
                state.permittedCustomersLoading = false;
                const normalize = (list: any[]) => list
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(c => c.preferences
                        ? { ...c, preferences: normalizePreferenceKeys(c.preferences) }
                        : c
                    );
                if (Array.isArray(action.payload)) {
                    state.permittedCustomers = normalize(action.payload);
                }
                else
                    state.permittedCustomers = normalize([action.payload]);
                state.permittedCustomersError = '';
            })
            .addCase(fetchAllPermittedCustomers.rejected, (state, action) => {
                state.permittedCustomersLoading = false;
                state.permittedCustomersError = action.payload as string;
            })
            .addCase(fetchAllCustomerSites.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.customerSites.length === 0) {
                    state.customerSitesLoading = true;
                }
                state.customerSitesError = null;
            })
            .addCase(fetchAllCustomerSites.fulfilled, (state, action: PayloadAction<CustomerSite[]>) => {
                state.customerSitesLoading = false;              
                if (Array.isArray(action.payload)) {
                    state.customerSites = action.payload
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(site => {
                            if (site.preferences?.present_components) {
                                const normalized = site.preferences.present_components.map(normalizeComponentName);
                                return {
                                    ...site,
                                    preferences: {
                                        ...site.preferences,
                                        present_components: Array.from(new Set(normalized)) as string[]
                                    }
                                };
                            }
                            return site;
                        });
                }
            })
            .addCase(fetchAllCustomerSites.rejected, (state, action) => {
                state.customerSitesError = action.payload as string;
                if (action.payload && (action.payload === 'Request canceled')) {
                    // Keep loading state if no data exists
                    state.customerSitesLoading = state.customerSites.length === 0;
                }
                else state.customerSitesLoading = false;
            })
            .addCase(fetchGeneralDetails.pending, (state) => {
                state.generalDetailsLoading = true;
                state.generalDetails = {} as GeneralDetails;
                state.generalDetailsError = null;
            })
            .addCase(fetchGeneralDetails.fulfilled, (state, action: PayloadAction<GeneralDetails>) => {
                state.generalDetailsLoading = false;
                state.generalDetails = action.payload;
            })
            .addCase(fetchGeneralDetails.rejected, (state, action) => {
                state.generalDetailsLoading = false;
                state.generalDetailsError = action.payload as string;
            })
            .addCase(fetchLocationFromHistory.pending, (state) => {
                state.loadingLocationHistory = true;
                state.locationHistory = [];
                state.locationHistoryError = null;
            })
            .addCase(fetchLocationFromHistory.fulfilled, (state, action: PayloadAction<AddressLocation[]>) => {
                state.loadingLocationHistory = false;
                state.locationHistory = action.payload;
            })
            .addCase(fetchLocationFromHistory.rejected, (state, action) => {
                state.loadingLocationHistory = false;
                state.locationHistoryError = action.payload as string;
            });
    },
});

export const { setSelectedKey, setSelectedRoute, setCollapsed, clearUserInfo, clearPermittedCustomers, clearWeatherInfo, setSelectedCustomerName, setSelectedSite, setIconTheme, setFromDate, setToDate, setSelectedTimezoneAbb, setSelectedTimezone } = userInfoSlice.actions;
export default userInfoSlice.reducer;
